import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Leaf, Users, TrendingUp, Star, ArrowRight, MapPin, Clock, ShoppingBag } from 'lucide-react';
import Button from '../../components/ui/Button';

const Homepage = () => {
  const navigate = useNavigate();

  // Available bags data that will be displayed on homepage
  const availableBags = [
  {
    id: 1,
    business: 'Café Tapatío',
    image: "https://images.unsplash.com/photo-1521099238480-47462c152711",
    alt: 'Deliciosa selección de café, pasteles y pan dulce mexicano del Café Tapatío',
    originalPrice: 150,
    discountPrice: 50,
    category: 'Café y Panadería',
    distance: '0.8 km',
    rating: 4.8,
    pickupTime: '18:00 - 20:00',
    available: 5
  },
  {
    id: 2,
    business: 'Restaurante La Chata',
    image: "https://images.unsplash.com/photo-1598763773372-127da881eb84",
    alt: 'Auténtica comida mexicana con tacos, enchiladas y platillos tradicionales',
    originalPrice: 280,
    discountPrice: 90,
    category: 'Comida Mexicana',
    distance: '1.2 km',
    rating: 4.6,
    pickupTime: '19:00 - 21:00',
    available: 3
  },
  {
    id: 3,
    business: 'Panadería San José',
    image: "https://images.unsplash.com/photo-1592135091406-308008ca5c2c",
    alt: 'Variedad de pan fresco, pasteles y productos de panadería artesanal',
    originalPrice: 120,
    discountPrice: 40,
    category: 'Panadería',
    distance: '0.5 km',
    rating: 4.9,
    pickupTime: '17:30 - 19:30',
    available: 8
  }];


  const handleImageError = (e) => {
    e.target.src = '/assets/images/no_image.png';
    e.target.alt = 'Imagen no disponible';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      {/* Hero Section */}
      <section className="relative px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Reduciendo el Desperdicio Alimentario en{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                  Guadalajara
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Únete a nuestra iniciativa para rescatar alimentos, apoyar a estudiantes y crear una ciudad más sostenible.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg px-8 py-4"
                  onClick={() => navigate('/signup')}>

                  Comenzar Ahora
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
            
            {/* Enhanced Right Side Card */}
            <div className="relative">
              <div className="bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 rounded-2xl p-8 shadow-2xl text-white">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Impacto Actual</h3>
                    <p className="text-green-100">Guadalajara, Jalisco</p>
                  </div>
                  <Leaf className="h-12 w-12 text-green-200" />
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-1">2,847</div>
                    <div className="text-green-100 text-sm">Bolsas Rescatadas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-1">1,423</div>
                    <div className="text-green-100 text-sm">Kg de CO₂ Evitado</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-1">89</div>
                    <div className="text-green-100 text-sm">Negocios Partner</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-1">₱34,567</div>
                    <div className="text-green-100 text-sm">Ahorro Estudiantes</div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-green-400">
                  <div className="flex items-center justify-between">
                    <span className="text-green-100">Meta Mensual</span>
                    <span className="text-white font-semibold">78% Completada</span>
                  </div>
                  <div className="mt-2 bg-green-400 rounded-full h-3">
                    <div className="bg-white rounded-full h-3" style={{ width: '78%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Available Bags Section - Directly on Homepage */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Bolsas Disponibles Ahora
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Descubre las bolsas sorpresa disponibles cerca de ti en Guadalajara
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {availableBags?.map((bag) =>
            <div key={bag?.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-gray-100">
                <div className="relative">
                  <img
                  src={bag?.image}
                  alt={bag?.alt || `Bolsa sorpresa de ${bag?.business}`}
                  className="w-full h-48 object-cover"
                  onError={handleImageError}
                  loading="lazy" />

                  <div className="absolute top-4 right-4 bg-green-600 text-white px-2 py-1 rounded-full text-sm font-medium">
                    {bag?.available} disponibles
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg text-gray-900">{bag?.business}</h3>
                    <div className="flex items-center text-yellow-500">
                      <Star className="h-4 w-4 fill-current" />
                      <span className="text-sm ml-1 text-gray-600">{bag?.rating}</span>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm mb-3">{bag?.category}</p>

                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <MapPin className="h-4 w-4 mr-1" />
                    <span>{bag?.distance}</span>
                    <Clock className="h-4 w-4 ml-4 mr-1" />
                    <span>{bag?.pickupTime}</span>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-bold text-green-600">
                        ${bag?.discountPrice}
                      </span>
                      <span className="text-sm text-gray-500 line-through">
                        ${bag?.originalPrice}
                      </span>
                    </div>
                    <div className="text-sm text-green-600 font-medium">
                      {Math.round((1 - bag?.discountPrice / bag?.originalPrice) * 100)}% desc.
                    </div>
                  </div>

                  <Button
                  className="w-full bg-green-600 hover:bg-green-700"
                  onClick={() => navigate(`/surprise-bag-details?id=${bag?.id}`)}>

                    <ShoppingBag className="h-4 w-4 mr-2" />
                    Reservar Bolsa
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* View More Button */}
          <div className="text-center">
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/available-bags')}
              className="border-green-600 text-green-600 hover:bg-green-50">

              Ver Todas las Bolsas Disponibles
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
      
      {/* Platform Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              ¿Cómo Funciona Food Loop?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conectamos a la comunidad de Guadalajara con negocios locales para rescatar alimentos y crear impacto positivo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="text-center p-8 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Para Estudiantes</h3>
              <p className="text-gray-600 mb-6">
                Accede a comida de calidad a precios subsidiados. Cada compra apoya la sostenibilidad de la ciudad.
              </p>
              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => navigate('/signup')}>

                Registrarse como Estudiante
              </Button>
            </div>

            <div className="text-center p-8 rounded-xl bg-gradient-to-br from-green-50 to-green-100 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Para Negocios</h3>
              <p className="text-gray-600 mb-6">
                Reduce desperdicios, genera ingresos adicionales y obtén certificación Zero Waste.
              </p>
              <Button
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
                onClick={() => navigate('/business-registration')}>

                Unirse Como Negocio
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Success Stories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Historias de Éxito
            </h2>
            <p className="text-xl text-gray-600">
              Conoce cómo Food Loop está transformando vidas en Guadalajara
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.unsplash.com/photo-1566887846814-c3fbb030d711"
                  alt="María González - Joven estudiante mexicana sonriendo, cabello castaño y playera universitaria"
                  className="w-16 h-16 rounded-full object-cover"
                  onError={handleImageError} />

                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">María González</h4>
                  <p className="text-gray-600">Estudiante de Ingeniería</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "Gracias a Food Loop he ahorrado más de $2,000 pesos mensuales en alimentación. Además, me siento orgullosa de contribuir al cuidado del medio ambiente."
              </p>
              <div className="flex text-yellow-400">
                {[...Array(5)]?.map((_, i) =>
                <Star key={i} className="h-5 w-5 fill-current" />
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center mb-6">
                <img
                  src="https://images.unsplash.com/photo-1655882752624-7c01aa90fa27"
                  alt="Carlos Ramírez - Hombre mexicano maduro con barba, propietario de restaurante local sonriendo"
                  className="w-16 h-16 rounded-full object-cover"
                  onError={handleImageError} />

                <div className="ml-4">
                  <h4 className="font-bold text-gray-900">Carlos Ramírez</h4>
                  <p className="text-gray-600">Propietario - Café Tapatío</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "En 6 meses hemos reducido nuestro desperdicio en 67% y generado ingresos adicionales de $15,000 pesos. La certificación nos ha posicionado como líderes en sostenibilidad."
              </p>
              <div className="flex text-yellow-400">
                {[...Array(5)]?.map((_, i) =>
                <Star key={i} className="h-5 w-5 fill-current" />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Únete a la Revolución Alimentaria
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Sé parte del cambio en Guadalajara. Cada acción cuenta para crear un futuro más sostenible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-green-600 text-lg px-8 py-4"
              onClick={() => navigate('/signup')}>

              Empezar Gratis
            </Button>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <img
                  src="/foodloop/assets/images/slazzer-preview-av4u0-1762512006110.png"
                  alt="Food Loop"
                  className="h-12 w-auto" />

              </div>
              <p className="text-gray-400 mb-4">
                Transformando el sistema alimentario de Guadalajara a través de la tecnología y la comunidad estudiantil.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Enlaces Rápidos</h4>
              <div className="space-y-2">
                <Link to="/about" className="text-gray-400 hover:text-white block">Acerca de</Link>
                <Link to="/impact-tracking" className="text-gray-400 hover:text-white block">Impacto</Link>
                <Link to="/contact" className="text-gray-400 hover:text-white block">Contacto</Link>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Universidad Panamericana</h4>
              <div className="space-y-2">
                <p className="text-gray-400">Campus Guadalajara</p>
                <p className="text-gray-400">Calzada Circunvalación Poniente #49</p>
                <p className="text-gray-400">Ciudad Granja, Zapopan, Jalisco</p>
                <p className="text-gray-400">foodloop@up.edu.mx</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Food Loop - Guadalajara. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default Homepage;